// Phase 10b — 3-tier Premium page: FREE | PREMIUM EVENT ($19.99) | ALLVIEWS PRO ($9.99/mo | $99/yr)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { fetchPricing, createCheckout, verifyCheckout, formatCents } from '@/lib/premium-api';
import { getProPricing, getProStatus, createProCheckout, verifyProCheckout } from '@/lib/pro-api';
import { getLookbook, refreshLookbookWatermark } from '@/lib/lookbook-api';
import TierBadge from '@/components/TierBadge';
import { ArrowLeft, Check, CheckCircle, AlertTriangle, XCircle, RefreshCw, TrendingUp } from 'lucide-react';

const POLL_MS = 1500;
const POLL_MAX = 40;

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

export default function HostPremiumPage() {
  const { user, refreshUser } = useAuth();
  const { ready } = useRequireAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [pricing, setPricing] = useState(null);
  const [proPricing, setProPricing] = useState(null);
  const [proSnap, setProSnap] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('MONTHLY');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({ kind: 'idle' });
  const [flash, setFlash] = useState(null);
  const [postUpgrade, setPostUpgrade] = useState({ kind: 'hidden' });
  const cancelRef = useRef(false);

  const isPro = user?.account_type === 'PROFESSIONAL';

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pr, pp, ps] = await Promise.all([
        eventId ? fetchPricing(eventId) : Promise.resolve(null),
        getProPricing(user?.business_category || null),
        getProStatus(),
      ]);
      if (pr) setPricing(pr);
      setProPricing(pp);
      setProSnap(ps.subscription);
      const rec = isPro
        ? (pp.recommended_plan_for_professional || 'ANNUAL')
        : (pp.recommended_plan_for_individual || 'MONTHLY');
      setSelectedPlan(rec);
    } catch (e) { setError(e?.message || 'Could not load pricing.'); }
    finally { setLoading(false); }
  }, [eventId, isPro, user?.business_category]);

  useEffect(() => { cancelRef.current = false; if (ready) load(); return () => { cancelRef.current = true; }; }, [ready, load]);

  // Handle Stripe return via URL session_id
  useEffect(() => {
    const sid = sp.get('session_id');
    const kind = sp.get('kind'); // 'event' or 'pro'
    if (!sid || !ready || busy.kind !== 'idle') return;
    if (kind === 'pro') pollProVerify(sid, 'UNKNOWN');
    else if (eventId) pollEventVerify(sid);
  }, [sp, ready]);  

  const isEventPremium = pricing?.is_premium === true;
  const isEventLegacy = pricing?.is_legacy_uncapped === true;
  const proActive = Boolean(proSnap?.active);
  const proPlanNow = proSnap?.plan;
  const proHeadline = proPricing?.headline || 'Every event, always premium.';
  const isBusyEvent = busy.kind === 'event-checkout' || busy.kind === 'event-polling';
  const isBusyPro = busy.kind === 'pro-checkout' || busy.kind === 'pro-polling';

  // ---- Event upgrade ----
  const pollEventVerify = useCallback(async (sid) => {
    let a = 0;
    while (a < POLL_MAX && !cancelRef.current) {
      a++;
      setBusy({ kind: 'event-polling', sessionId: sid, attempts: a });
      try {
        const v = await verifyCheckout(eventId, sid);
        if (v.fulfilled) {
          setFlash({ tone: 'success', text: "You're on Premium Event.", detail: `Charged ${formatCents(v.payment.amount_cents, v.payment.currency)}. Every cap is lifted; watermark drops off served images now.` });
          setBusy({ kind: 'idle' }); await load();
          try { const lb = await getLookbook(eventId); if (lb.manifest?.state === 'LIVE' && lb.manifest?.watermark) setPostUpgrade({ kind: 'ready' }); } catch { /* ok */ }
          return;
        }
        if (v.payment.status === 'EXPIRED' || v.payment.status === 'FAILED') {
          setFlash({ tone: 'warn', text: v.payment.status === 'EXPIRED' ? 'Stripe session expired.' : 'Payment failed.', detail: 'No charge was made.' }); setBusy({ kind: 'idle' }); return;
        }
      } catch (e) { if (a >= POLL_MAX) { setFlash({ tone: 'warn', text: "Couldn't confirm in time.", detail: e?.message || '' }); setBusy({ kind: 'idle' }); return; } }
      await new Promise(r => setTimeout(r, POLL_MS));
    }
    setBusy({ kind: 'idle' });
  }, [eventId, load]);

  const onEventUpgrade = useCallback(async () => {
    if (!eventId || !pricing?.premium_available) return;
    setBusy({ kind: 'event-checkout' }); setFlash(null);
    try {
      const co = await createCheckout(eventId, window.location.origin);
      window.location.href = co.checkout_url;
    } catch (e) {
      setBusy({ kind: 'idle' });
      if (e?.code === 'ALREADY_PREMIUM') { window.alert('Already on Premium.'); load(); }
      else window.alert(e?.message || 'Upgrade failed.');
    }
  }, [eventId, pricing, load]);

  // ---- PRO subscription ----
  const pollProVerify = useCallback(async (sid, plan) => {
    let a = 0;
    while (a < POLL_MAX && !cancelRef.current) {
      a++;
      setBusy({ kind: 'pro-polling', sessionId: sid, plan, attempts: a });
      try {
        const v = await verifyProCheckout(sid);
        if (v.fulfilled) {
          setFlash({ tone: 'success', text: 'Welcome to ALLVIEWS PRO.', detail: 'Every event you host is premium from now on. Your referral code is minted and ready in Referrals.' });
          setBusy({ kind: 'idle' }); setProSnap(v.subscription);
          await refreshUser(); await load(); return;
        }
      } catch (e) { if (a >= POLL_MAX) { setFlash({ tone: 'warn', text: "Couldn't confirm subscription.", detail: e?.message || '' }); setBusy({ kind: 'idle' }); return; } }
      await new Promise(r => setTimeout(r, POLL_MS));
    }
    setBusy({ kind: 'idle' });
  }, [load, refreshUser]);

  const onProSubscribe = useCallback(async (plan) => {
    setBusy({ kind: 'pro-checkout', plan }); setFlash(null);
    try {
      const co = await createProCheckout(plan, window.location.origin);
      window.location.href = co.checkout_url;
    } catch (e) {
      setBusy({ kind: 'idle' });
      if (e?.code === 'ALREADY_SUBSCRIBED') { window.alert('Already subscribed.'); load(); }
      else window.alert(e?.message || 'Subscription failed.');
    }
  }, [load]);

  const onRepublish = useCallback(async () => {
    setPostUpgrade({ kind: 'running' });
    try { const r = await refreshLookbookWatermark(eventId); setPostUpgrade({ kind: 'done', changed: r.changed }); }
    catch (e) { setPostUpgrade({ kind: 'error', message: e?.message || 'Republish failed.' }); }
  }, [eventId]);

  if (!ready) return null;
  if (user?.role !== 'HOST' && user?.role !== 'SUPER_ADMIN') {
    return <div className="max-w-2xl mx-auto px-4 py-8" data-testid="host-premium-forbidden"><h1 className="text-2xl font-extrabold text-[#0B1B46]">Not available</h1></div>;
  }

  const selectedTile = proPricing?.plans?.find(p => p.plan === selectedPlan);
  const tierBadge = proActive ? 'PRO' : isEventPremium ? 'PREMIUM_EVENT' : isEventLegacy ? 'LEGACY' : 'FREE';

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="host-premium-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="host-premium-back"><ArrowLeft className="w-4 h-4" /> back</button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] font-extrabold tracking-[1.5px] text-[#146BFF] mb-1">ALLVIEWS PLANS</p>
          <h1 className="text-2xl font-extrabold text-[#0B1B46]" data-testid="host-premium-title">
            {proActive ? "You\u2019re on ALLVIEWS PRO." : isEventPremium ? 'This event is on Premium.' : 'Pick your plan'}
          </h1>
          <p className="text-sm text-[#5B6472] mt-1">
            {proActive ? `Every event you host is fully premium. Renews ${fmtDate(proSnap?.current_period_end)}.`
             : isPro ? proHeadline
             : 'One event upgrade or every event forever \u2014 your call.'}
          </p>
        </div>
        <TierBadge tier={tierBadge} testId="host-premium-current-tier" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="host-premium-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="host-premium-error"><p className="text-sm text-[#5B6472]">{error}</p><button onClick={load} className="h-10 px-6 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg">Retry</button></div>
      ) : (
        <>
          {/* Flash */}
          {flash && <FlashBar flash={flash} />}

          {/* Post-upgrade republish */}
          {postUpgrade.kind !== 'hidden' && (
            <div className="bg-[#F4F8FF] border border-[#146BFF] rounded-xl p-4 mb-4" data-testid="host-premium-republish-card">
              <div className="flex items-start gap-3"><RefreshCw className="w-5 h-5 text-[#146BFF] mt-0.5 flex-shrink-0" /><div className="flex-1"><p className="text-[15px] font-extrabold text-[#0B1B46]">Refresh your live lookbook</p><p className="text-[13px] text-[#0B1B46] mt-0.5 leading-[18px]">Your existing share link keeps working. The watermark drops off served images immediately.</p></div></div>
              <div className="mt-3">
                {postUpgrade.kind === 'ready' && <button onClick={onRepublish} className="w-full h-10 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors" data-testid="host-premium-republish-btn">Republish without watermark</button>}
                {postUpgrade.kind === 'running' && <button disabled className="w-full h-10 bg-[#146BFF] opacity-60 text-white font-semibold rounded-lg">Refreshing&hellip;</button>}
                {postUpgrade.kind === 'done' && <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#1F8A55]" /><span className="text-[#1F8A55] font-bold text-sm" data-testid="host-premium-republish-done">{postUpgrade.changed ? 'Watermark removed \u2014 live now.' : 'Already up to date.'}</span></div>}
                {postUpgrade.kind === 'error' && <><p className="text-[13px] text-[#C93434]">{postUpgrade.message}</p><button onClick={onRepublish} className="h-9 px-4 mt-2 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm">Try again</button></>}
              </div>
            </div>
          )}

          {/* ═══ FREE tile ═══ */}
          {pricing && (
            <TierCard active={!isEventPremium && !isEventLegacy && !proActive} testId="host-premium-free-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl font-extrabold text-[#0B1B46]">Free</span>
                {!isEventPremium && !isEventLegacy && !proActive && <CurrentBadge testId="host-premium-free-current" />}
              </div>
              <p className="text-[22px] font-extrabold text-[#0B1B46] mt-2">Free forever</p>
              <FeatureList features={pricing.free_features || []} muted />
            </TierCard>
          )}

          {/* ═══ PREMIUM EVENT tile ($19.99 one-time) ═══ */}
          {pricing && (
            <TierCard highlight={pricing.premium_available && !isEventPremium && !proActive} success={isEventPremium} testId="host-premium-premium-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl font-extrabold text-[#0B1B46]">Premium Event</span>
                {isEventPremium && <CurrentBadge testId="host-premium-premium-current" />}
                {proActive && !isEventPremium && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]" data-testid="host-premium-included-in-pro">★ Included in PRO</span>}
                {!isEventPremium && !proActive && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]">For this event</span>}
              </div>
              <p className="mt-2" data-testid="host-premium-price">
                <span className="text-[22px] font-extrabold text-[#0B1B46]">{formatCents(pricing.price_cents, pricing.currency)}</span>
                <span className="text-[13px] font-semibold text-[#5B6472] ml-1">&middot; one-time, per event</span>
              </p>
              <FeatureList features={pricing.premium_features || []} />
              {proActive ? (
                <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-[#E4F4EC]" data-testid="host-premium-included-in-pro-notice"><CheckCircle className="w-4 h-4 text-[#1F8A55]" /><span className="text-[13px] font-bold text-[#1F8A55]">Included in your PRO plan.</span></div>
              ) : pricing.premium_available ? (
                <button onClick={onEventUpgrade} disabled={isBusyEvent || isBusyPro} className={`w-full h-11 mt-4 font-semibold rounded-lg transition-colors ${isBusyEvent || isBusyPro ? 'bg-[#146BFF]/60 text-white cursor-not-allowed' : 'bg-[#146BFF] hover:bg-[#0F55CC] text-white'}`} data-testid="host-premium-upgrade-btn">
                  {isBusyEvent ? (busy.kind === 'event-polling' ? `Confirming\u2026 (${busy.attempts}/${POLL_MAX})` : 'Opening Stripe\u2026') : `Unlock for ${formatCents(pricing.price_cents, pricing.currency)}`}
                </button>
              ) : null}
            </TierCard>
          )}

          {/* ═══ ALLVIEWS PRO tile (recurring) ═══ */}
          {proPricing && (
            <TierCard highlight={!proActive && isPro} success={proActive} testId="host-premium-pro-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xl font-extrabold text-[#0B1B46]">ALLVIEWS PRO</span>
                {proActive && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E4F4EC] text-[#1F8A55]" data-testid="host-premium-pro-current">✓ Current &middot; {proPlanNow === 'ANNUAL' ? 'Annual' : 'Monthly'}</span>}
                {!proActive && isPro && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]" data-testid="host-premium-pro-recommended">★ Recommended for pros</span>}
              </div>
              {isPro && <p className="text-[15px] font-bold text-[#146BFF] mt-2 leading-[22px]" data-testid="host-premium-pro-headline">{proHeadline}</p>}

              {proActive ? (
                <p className="text-[13px] text-[#5B6472] mt-1">Renews {fmtDate(proSnap?.current_period_end)}{proSnap?.cancel_at_period_end ? ' · cancellation scheduled' : ''}</p>
              ) : (
                <>
                  {/* Plan toggle */}
                  <div className="flex gap-3 mt-4" data-testid="host-premium-pro-plan-toggle">
                    {proPricing.plans.map(tile => {
                      const act = selectedPlan === tile.plan;
                      return (
                        <button key={tile.plan} onClick={() => setSelectedPlan(tile.plan)}
                          className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${act ? 'border-[#146BFF] bg-[#EFF5FF]' : 'border-[#E3E8EF] bg-white hover:bg-[#F3F5F8]'}`}
                          data-testid={`host-premium-pro-plan-${tile.plan.toLowerCase()}`}>
                          <span className={`text-[11px] font-extrabold tracking-wide uppercase ${act ? 'text-[#146BFF]' : 'text-[#5B6472]'}`}>{tile.plan === 'MONTHLY' ? 'Monthly' : 'Annual'}</span>
                          <p className="mt-1"><span className="text-lg font-extrabold text-[#0B1B46]">{formatCents(tile.price_cents, tile.currency)}</span><span className="text-[13px] font-semibold text-[#5B6472]">{tile.plan === 'MONTHLY' ? ' / mo' : ' / yr'}</span></p>
                          {tile.savings_copy && <span className="text-[11px] font-bold text-[#1F8A55] mt-1 block">{tile.savings_copy}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {/* Break-even nudge for individuals */}
                  {!isPro && pricing && (() => {
                    const annual = proPricing.plans.find(p => p.plan === 'ANNUAL');
                    if (!annual || !pricing.price_cents) return null;
                    const events = Math.ceil(annual.price_cents / pricing.price_cents);
                    const totalCents = events * pricing.price_cents;
                    return (
                      <div className="flex items-start gap-1.5 mt-3 py-2 px-3 rounded-lg bg-[#EFF5FF]" data-testid="host-premium-pro-break-even">
                        <TrendingUp className="w-3.5 h-3.5 text-[#146BFF] mt-0.5 flex-shrink-0" />
                        <span className="text-xs font-semibold text-[#0B1B46] leading-[17px]">{events} premium events = {formatCents(totalCents, pricing.currency)} &mdash; PRO is {formatCents(annual.price_cents, annual.currency)}/yr, every event included.</span>
                      </div>
                    );
                  })()}
                </>
              )}

              <FeatureList features={proPricing.features || []} />

              {proActive ? (
                <button onClick={() => navigate('/settings')} className="w-full h-10 mt-4 border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors" data-testid="host-premium-pro-manage-btn">Manage subscription</button>
              ) : (
                <>
                  <button onClick={() => onProSubscribe(selectedPlan)} disabled={isBusyPro || isBusyEvent}
                    className={`w-full h-11 mt-4 font-semibold rounded-lg transition-colors ${isBusyPro || isBusyEvent ? 'bg-[#146BFF]/60 text-white cursor-not-allowed' : 'bg-[#146BFF] hover:bg-[#0F55CC] text-white'}`}
                    data-testid="host-premium-pro-subscribe-btn">
                    {isBusyPro ? (busy.kind === 'pro-polling' ? `Confirming\u2026 (${busy.attempts}/${POLL_MAX})` : 'Opening Stripe\u2026')
                     : `Start ${selectedPlan === 'MONTHLY' ? 'Monthly' : 'Annual'} \u00b7 ${selectedTile ? formatCents(selectedTile.price_cents, selectedTile.currency) : 'PRO'}`}
                  </button>
                  <p className="text-[11px] text-[#5B6472] text-center mt-2 leading-[16px]">Cancel anytime — the plan runs to the end of the billing period, no retroactive changes to already-published lookbooks.</p>
                </>
              )}
              <button onClick={() => navigate('/pro-referrals')} className="text-[13px] font-bold text-[#146BFF] mt-3 hover:underline text-left" data-testid="host-premium-pro-referrals-link">Earn 10% for life — see the PRO referral program &rarr;</button>
            </TierCard>
          )}

          <p className="text-center text-xs text-[#5B6472] mt-8 leading-[18px]" data-testid="host-premium-fine-print">
            Stripe test mode. Card <span className="font-mono text-[#0B1B46]">4242 4242 4242 4242</span>, any future expiry, any CVC, any postal. No real charge.
          </p>
        </>
      )}
    </div>
  );
}

function TierCard({ children, active, highlight, success, testId }) {
  const border = success ? 'border-[#1F8A55] border-2 bg-[#F4FBF6]'
    : active ? 'border-[#146BFF] border-2 bg-[#F4F8FF]'
    : highlight ? 'border-[#146BFF] border-2 shadow-[0_4px_12px_rgba(20,107,255,0.18)]'
    : 'border-[#E3E8EF]';
  return <div className={`bg-white border rounded-xl p-4 mt-4 ${border}`} data-testid={testId}>{children}</div>;
}
function CurrentBadge({ testId }) { return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#E4F4EC] text-[#1F8A55]" data-testid={testId}><Check className="w-3 h-3" /> Current plan</span>; }
function FeatureList({ features, muted }) {
  return <div className="mt-3 flex flex-col gap-1.5">{features.map((f, i) => (
    <div key={i} className="flex items-start gap-2"><Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${muted ? 'text-[#5B6472]' : 'text-[#1F8A55]'}`} /><span className="text-sm text-[#0B1B46]">{f}</span></div>
  ))}</div>;
}
function FlashBar({ flash }) {
  const colors = { success: ['bg-[#E4F4EC] border-[#1F8A55]', 'text-[#1F8A55]'], warn: ['bg-[#FFF6E5] border-[#B86500]', 'text-[#B86500]'], danger: ['bg-[#FBE5E5] border-[#C93434]', 'text-[#C93434]'] };
  const [bgBorder, textColor] = colors[flash.tone] || colors.warn;
  const Icon = flash.tone === 'success' ? CheckCircle : flash.tone === 'warn' ? AlertTriangle : XCircle;
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border mb-4 ${bgBorder}`} data-testid={`host-premium-flash-${flash.tone}`}>
      <Icon className={`w-4 h-4 ${textColor} mt-0.5 flex-shrink-0`} /><div className="flex-1"><p className={`text-sm font-bold ${textColor}`}>{flash.text}</p>{flash.detail && <p className="text-[13px] text-[#5B6472] mt-1 leading-[18px]">{flash.detail}</p>}</div>
    </div>
  );
}
